import { RESERVED_ORGANIZATION_SLUGS } from "@gioia365/contracts";
import type { LocationRewrite } from "@tanstack/react-router";
import { readTabContext, tabKey, type TabContext } from "./tab-context";

// Il nome dell'azienda in tutti gli indirizzi delle sue pagine (decisione del product owner del 4 ottobre 2026: «CERTO»):
// la Home di Academy è `/academy`, l'elenco dei Lead `/academy/lead`, il Team `/academy/impostazioni/team`. Il router
// lavora con i percorsi di sempre (`/`, `/lead`, `/impostazioni/team`): il `rewrite` toglie l'indirizzo breve dell'azienda
// da ciò che arriva dal browser e lo rimette davanti a ciò che il router scrive, così nessun collegamento del programma
// cambia. Le pagine pubbliche, quelle tecniche e quelle della piattaforma (`/login`, `/scegli-azienda`, `/gioia365/…`)
// restano senza. Quale azienda dice l'indirizzo lo decide la guardia dell'azienda nel router, che controlla con le
// appartenenze della persona (o, per il Super admin di Gioia365, con le aziende attive) e cambia l'azienda della scheda.

/** I primi pezzi degli indirizzi che non sono di un'azienda: nessun nome d'azienda davanti. */
export const OUTSIDE_COMPANY: ReadonlySet<string> = new Set([
  "acquisto",
  "registrazione",
  "abbonamento",
  "login",
  "reimposta-password",
  "invito",
  "styleguide",
  "scegli-azienda",
  "cambia-azienda",
  "cambia-vista",
  "esci",
  "gioia365",
]);

/** La chiave dello `Storage` della scheda con l'indirizzo breve della sua azienda. */
export const TAB_ADDRESS_STORAGE_KEY = "gioia365.scheda.indirizzo";

/** Il primo pezzo di un percorso, in minuscolo, oppure "" per la radice. */
function firstSegment(pathname: string): string {
  return (pathname.split("/")[1] ?? "").toLowerCase();
}

/**
 * L'indirizzo breve dell'azienda che un indirizzo del browser porta davanti, oppure `null`: il primo pezzo, se non è una
 * pagina del programma (`RESERVED_ORGANIZATION_SLUGS`, che le aziende non possono usare come indirizzo).
 */
export function companySlugOf(publicHref: string): string | null {
  const pathname = publicHref.split(/[?#]/)[0] ?? "";
  const first = decodedOrRaw(firstSegment(pathname));
  return first === "" || RESERVED_ORGANIZATION_SLUGS.has(first) ? null : first;
}

/** Un pezzo d'indirizzo scritto male (`%E0%A4`) resta com'è: non è l'indirizzo di nessuna azienda, ma non rompe la pagina. */
function decodedOrRaw(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * L'indirizzo breve dell'azienda della scheda, se è stato scritto per l'azienda di adesso: vale insieme al contesto della
 * scheda (`tabKey`), quindi un'azienda cambiata senza riscriverlo non porta mai l'indirizzo di un'altra.
 */
export function readTabAddress(storage: Storage, tab: TabContext): string | null {
  const raw = storage.getItem(TAB_ADDRESS_STORAGE_KEY);
  if (raw === null) return null;
  try {
    const value = JSON.parse(raw) as { tab?: unknown; slug?: unknown };
    return value.tab === tabKey(tab) && typeof value.slug === "string" ? value.slug : null;
  } catch {
    return null;
  }
}

export function writeTabAddress(storage: Storage, tab: TabContext, slug: string): void {
  storage.setItem(TAB_ADDRESS_STORAGE_KEY, JSON.stringify({ tab: tabKey(tab), slug }));
}

export function clearTabAddress(storage: Storage): void {
  storage.removeItem(TAB_ADDRESS_STORAGE_KEY);
}

/** Il `rewrite` del router di una scheda: toglie l'indirizzo dell'azienda in entrata e lo rimette in uscita. */
export function companyAddressRewrite(tabStorage: Storage): LocationRewrite {
  return {
    input: ({ url }) => {
      if (companySlugOf(url.pathname) === null) return undefined;
      const next = new URL(url.href);
      next.pathname = `/${url.pathname.split("/").slice(2).join("/")}`;
      return next;
    },
    output: ({ url }) => {
      if (OUTSIDE_COMPANY.has(firstSegment(url.pathname))) return undefined;
      const tab = readTabContext(tabStorage);
      const slug = tab ? readTabAddress(tabStorage, tab) : null;
      if (!slug) return undefined;
      const next = new URL(url.href);
      next.pathname = url.pathname === "/" ? `/${slug}` : `/${slug}${url.pathname}`;
      return next;
    },
  };
}
